'use client'

import { memo } from 'react'
import Link from 'next/link'
import {
  Building,
  CloudOff,
  FileSearch,
  Gavel,
  Globe2,
  Landmark,
  Lock,
  MapPin,
  ScrollText,
  Server,
  ShieldCheck,
} from 'lucide-react'
import { NinxlGovtsHero, NINX_GOVTS_SALES_EMAIL } from './ninxl-govts-hero'
import {
  NinxlGovtsAgencyRoster,
  NinxlGovtsCitizenQueue,
  NinxlGovtsComplianceLedger,
  NinxlGovtsDossierManifest,
  NinxlGovtsPerimeterStack,
  NinxlGovtsSealDivider,
  NinxlGovtsSectionBackdrop,
} from './ninxl-govts-graphics'
import { NinxlGovtsScorecard } from './ninxl-govts-comparison-table'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'
import { Ninxl8BitDivider, Ninxl8BitFrame, Ninxl8BitSection } from './ninxl-8bit-ui'
import { NinxlEvervaultShell } from './ninxl-evervault-shell'
import { NinConnectPlatformSection } from '@/app/(marketing)/_components/nin-connect-platform-section'

export { NINX_GOVTS_SALES_EMAIL }

export const PUBLIC_SECTOR_CHALLENGES = [
  {
    ref: 'GOV-001',
    title: 'Citizen Data Exposure',
    desc: 'Foreign cloud AI routes PII, benefits records, and case files outside national borders.',
    icon: CloudOff,
  },
  {
    ref: 'GOV-002',
    title: 'Procurement Deadlock',
    desc: 'Legacy vendors and SaaS contracts block adoption of modern agentic workflows.',
    icon: Gavel,
  },
  {
    ref: 'GOV-003',
    title: 'Siloed Departments',
    desc: 'Ministries run disconnected systems with no shared, auditable AI execution layer.',
    icon: Building,
  },
  {
    ref: 'GOV-004',
    title: 'Multilingual Service Gaps',
    desc: 'Citizen-facing AI must operate across regional languages without external APIs.',
    icon: Globe2,
  },
] as const

export const GOVT_WORKFLOW_STEPS = [
  { step: 'Intake', detail: 'Citizen submits benefits eligibility request.', status: 'RECEIVED' },
  { step: 'Classify', detail: 'PII flagged and routed inside national perimeter.', status: 'CLASSIFIED' },
  { step: 'Verify', detail: 'Cross-checks department records and prior cases.', status: 'VERIFIED' },
  { step: 'Draft', detail: 'Officer-ready response document generated.', status: 'DRAFTED' },
  { step: 'Approve', detail: 'Routed to authorized reviewer with audit trail.', status: 'PENDING' },
  { step: 'Archive', detail: 'Immutable log stored for legislative review.', status: 'ARCHIVED' },
] as const

const PERIMETER_LAYERS = [
  { tier: 'TIER 0', title: 'Citizen Request', desc: 'Every case enters through authenticated government channels with PII classification.' },
  { tier: 'TIER 1', title: 'Policy Gate', desc: 'Data localization rules and egress blocks enforced before any model runs.' },
  { tier: 'TIER 2', title: 'Sovereign Engine', desc: 'Inference executes on ministry-controlled hardware with zero foreign transmission.' },
  { tier: 'TIER 3', title: 'Audit Archive', desc: 'Immutable action logs retained for legislative, judicial, and RTI oversight.' },
] as const

const COMPLIANCE_ENTRIES = [
  { code: 'POL-01', title: 'CASA Certified', desc: 'Independent security audit for desktop agent deployments.', icon: ShieldCheck },
  { code: 'POL-02', title: 'SOC 2 in Progress', desc: 'Type II audit underway for security and availability controls.', icon: Lock },
  {
    code: 'POL-03',
    title: 'ZDR Policy',
    desc: 'On-device stays on ministry systems. Optional cloud credits use Zero Data Retention.',
    icon: CloudOff,
  },
  { code: 'POL-04', title: 'Immutable Audit Logs', desc: 'Every AI action recorded for legislative and judicial review.', icon: ScrollText },
  { code: 'POL-05', title: 'Policy Gates', desc: 'Enforce data localization and usage rules at runtime.', icon: FileSearch },
  { code: 'POL-06', title: 'Air-Gapped Nodes', desc: 'Field offices and ministries run without public internet.', icon: Server },
] as const

const AGENCY_ROSTER = [
  { code: 'MIN-01', title: 'Central Ministries', desc: 'National programs with cross-department citizen records.', icon: Landmark },
  { code: 'MUN-02', title: 'Municipal Corporations', desc: 'Permits, grievances, and local service delivery at scale.', icon: MapPin },
  { code: 'JUD-03', title: 'Judiciary & Legal', desc: 'Case ingestion and drafting inside a secured local perimeter.', icon: Gavel },
  { code: 'HLT-04', title: 'Public Health', desc: 'Patient and population health data never leaves the facility.', icon: ShieldCheck },
] as const

const BRIEFING_CHECKLIST = [
  'Sovereign licensing for ministry-scale rollout',
  'Legacy ERP, records, and portal integration',
  'ZDR: on-device local · cloud credits zero retention',
  'Dedicated government security and compliance support',
] as const

export const NinxlGovtsSection = memo(function NinxlGovtsSection() {
  return (
    <>
      <NinxlGovtsHero />

      <NinxlGovtsSealDivider label="DOSSIER" />

      {/* CHALLENGES: manifest list */}
      <Ninxl8BitSection data-section="dossier" className="relative overflow-hidden">
        <NinxlGovtsSectionBackdrop variant="dossier" />
        <div className="mx-auto max-w-4xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">THE CHALLENGE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Public sector risk manifest
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Four systemic risks blocking sovereign AI adoption in government: filed, numbered, and addressable.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlGovtsDossierManifest items={PUBLIC_SECTOR_CHALLENGES} />
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="PERIMETER" />

      {/* PERIMETER: layered stack */}
      <Ninxl8BitSection alt data-section="perimeter" className="relative overflow-hidden">
        <NinxlGovtsSectionBackdrop variant="perimeter" />
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <NinxlScrollReveal variant="left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">DATA SOVEREIGNTY</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-4 text-left text-lg md:text-xl">
                The citizen data perimeter
              </h2>
              <p className="ninxl-8bit-body mb-6 text-left">
                Four stacked tiers from citizen intake to audit archive. No foreign cloud in the chain.
              </p>
              <div className="ninxl-govts-terminal ninxl-pixel-border relative overflow-hidden">
                <NinxlEvervaultShell size="md" contentClassName="p-4 text-left">
                  <div className="ninxl-8bit-label mb-2">EGRESS STATUS</div>
                  <div className="space-y-1 font-mono text-sm">
                    <div className="ninxl-govts-terminal-line">&gt; FOREIGN CLOUD: BLOCKED</div>
                    <div className="ninxl-govts-terminal-line">&gt; CITIZEN PII: LOCAL ONLY</div>
                    <div className="ninxl-govts-terminal-line">&gt; AUDIT MODE: IMMUTABLE</div>
                  </div>
                </NinxlEvervaultShell>
              </div>
            </NinxlScrollReveal>
            <NinxlScrollReveal variant="right" delay={0.08}>
              <NinxlGovtsPerimeterStack layers={PERIMETER_LAYERS} />
            </NinxlScrollReveal>
          </div>
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="QUEUE" />

      {/* WORKFLOWS: horizontal ticket queue */}
      <Ninxl8BitSection data-section="queue" className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">AGENTIC SERVICE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                The citizen case queue
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                A live processing lane, not a chatbot. Each ticket moves through classified stages on sovereign infrastructure.
              </p>
            </div>
          </NinxlScrollReveal>
          <Ninxl8BitFrame label="CASE QUEUE · LIVE">
            <NinxlGovtsCitizenQueue steps={GOVT_WORKFLOW_STEPS} />
          </Ninxl8BitFrame>
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="SCORECARD" />

      {/* ADVANTAGE: three-panel scorecard */}
      <Ninxl8BitSection alt data-section="scorecard">
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-center">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">WHY NINX</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Sovereign stack scorecard
              </h2>
              <p className="ninxl-8bit-body mx-auto max-w-2xl">
                Three procurement paths compared as official capability assessments, not a feature spreadsheet.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlScrollReveal delay={0.1}>
            <NinxlGovtsScorecard />
          </NinxlScrollReveal>
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="LEDGER" />

      {/* COMPLIANCE: policy ledger grid */}
      <Ninxl8BitSection data-section="ledger" className="relative overflow-hidden">
        <NinxlGovtsSectionBackdrop variant="ledger" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">TRUST & COMPLIANCE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Policy ledger
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Numbered governance controls for every ministry deployment: permissions, logs, gates, and air-gap readiness.
              </p>
            </div>
          </NinxlScrollReveal>
          <div className="ninxl-pixel-border relative overflow-hidden">
            <NinxlEvervaultShell contentClassName="p-4 md:p-5 text-left">
              <NinxlGovtsComplianceLedger entries={COMPLIANCE_ENTRIES} />
            </NinxlEvervaultShell>
          </div>
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="ROSTER" />

      {/* AGENCIES: horizontal department roster */}
      <Ninxl8BitSection alt data-section="roster" className="relative overflow-hidden">
        <NinxlGovtsSectionBackdrop variant="roster" />
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">PUBLIC SECTORS</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Department roster
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Scroll the roster: each agency code maps to a sovereign deployment profile.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlGovtsAgencyRoster agencies={AGENCY_ROSTER} />
        </div>
      </Ninxl8BitSection>

      <NinxlGovtsSealDivider label="CONNECT" />

      {/* CONNECT PLATFORM */}
      <Ninxl8BitSection id="connect-platform" data-section="connect-platform" className="scroll-mt-24">
        <div className="mx-auto max-w-6xl px-6">
          <NinConnectPlatformSection variant="govts" />
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <NinxlGovtsSealDivider label="BRIEFING" />

      {/* CTA: briefing dossier */}
      <Ninxl8BitSection className="py-20 md:py-24" data-section="contact">
        <NinxlScrollReveal variant="scale">
          <div className="mx-auto max-w-3xl px-6 pt-4">
            <div className="ninxl-govts-briefing-shell ninxl-pixel-border relative">
              <div className="ninxl-govts-briefing-tab ninxl-8bit-label">BRIEFING REQUEST · FORM-GOV-09</div>
              <NinxlEvervaultShell
                className="ninxl-govts-briefing-cipher"
                contentClassName="p-6 md:p-8 pt-8 text-left"
              >
                <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
                  Schedule a sovereign AI briefing
                </h2>
                <p className="ninxl-8bit-body mb-6 [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
                  Request a ministry or municipality pilot. Our government team will respond with a deployment assessment.
                </p>

                <ul className="mb-8 space-y-2">
                  {BRIEFING_CHECKLIST.map((item) => (
                    <li key={item} className="ninxl-govts-briefing-check flex items-start gap-3">
                      <Lock className="ninxl-theme-text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      <span className="ninxl-theme-text-muted text-sm">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <Link
                    href={`mailto:${NINX_GOVTS_SALES_EMAIL}?subject=NINX%20Govts%20Briefing%20Request`}
                    className="ninxl-pixel-btn ninxl-pixel-btn-primary pointer-events-auto"
                  >
                    Request Briefing
                  </Link>
                  <div className="ninxl-8bit-label flex items-center gap-2">
                    <FileSearch className="h-3.5 w-3.5" aria-hidden />
                    Pilot programs for regulated public-sector teams
                  </div>
                </div>
              </NinxlEvervaultShell>
            </div>
          </div>
        </NinxlScrollReveal>
      </Ninxl8BitSection>
    </>
  )
})